import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../../data";
import { Dialog, useAction } from "../../components/ui";
import { date, money } from "../../lib/format";
import { Fehler, Laden } from "./parts";

/**
 * Kunden · Zahlung auf Rechnung (0590): Bankverbindung für Überweisungen (platform_defaults, Admin und Entwicklung), offene und
 * erledigte Rechnungen der Abos auf Rechnung, „Zahlung von Hand verbuchen“ (Serverfunktion zahlungseingang markiert die Rechnung bei
 * Stripe als paid_out_of_band) und die eingegangenen Zahlungen aus den Payoneer-Mails mit ihrem Stand. Unklare Eingänge haben ein
 * Ticket (Kategorie Abrechnung).
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
type Bank = { iban: string; bic: string | null; holder: string } | null;
type Rechnung = {
  id: string; org_id: string; org_name: string; number: string | null; status: string; amount_due_cents: number; amount_remaining_cents: number; currency: string;
  customer_name: string | null; due_date: string | null; overdue: boolean; hosted_invoice_url: string | null; paid_at: string | null; paid_via: string | null; created_at: string | null;
};
type Eingang = {
  id: string; source: "mail" | "manual"; received_at: string; amount_cents: number | null; currency: string | null; reference: string | null; payer: string | null;
  provider_ref: string | null; sender_verified: boolean; status: string; reason: string | null; invoice: string | null; org_id: string | null; org_name: string | null;
  ticket_id: string | null; note: string | null; booked_by_email: string | null;
};
type Stand = { bank: Bank; invoices: Rechnung[]; payments: Eingang[] };

const STAND: Record<string, string> = { matched: "zugeordnet", unclear: "unklar", foreign: "fremd", error: "Fehler", neu: "neu" };
const GRUND: Record<string, string> = {
  mehrere_rechnungen: "mehrere Rechnungsnummern", schon_bezahlt: "schon bezahlt", rechnung_nicht_offen: "Rechnung nicht offen", waehrung_abweichend: "andere Währung",
  betrag_zu_niedrig: "Betrag zu niedrig", betrag_zu_hoch: "Betrag zu hoch", absender_ungeprueft: "Absender nicht geprüft", ohne_rechnungsnummer: "ohne Rechnungsnummer",
  keine_rechnung: "keine CreatorOffice-Rechnung", stripe_fehler: "Stripe lehnte ab",
};
const VIA: Record<string, string> = { stripe: "über Stripe", bank_mail: "Payoneer-Mail", manual: "von Hand" };
const betrag = (cents: number | null, cur: string | null) => (cents === null ? "–" : money(cents, cur ?? "EUR"));

export function Rechnungszahlung() {
  const q = useQuery({ queryKey: ["platform_invoice_payments"], queryFn: async () => parse<Stand>(await api.read("platform_invoice_payments", { p_days: 180 })) });
  const { busy, error, run } = useAction();
  const [bank, setBank] = useState<{ holder: string; iban: string; bic: string } | null>(null);
  const [buchen, setBuchen] = useState<Rechnung | null>(null);
  const [notiz, setNotiz] = useState("");
  const [eingang, setEingang] = useState("");

  const d = q.data;
  const form = bank ?? { holder: d?.bank?.holder ?? "", iban: d?.bank?.iban ?? "", bic: d?.bank?.bic ?? "" };
  const speichern = () => run("bank", async () => {
    // IBAN zuerst: eine falsche IBAN (Prüfziffer) bricht ab, bevor etwas anderes gespeichert ist
    for (const [key, wert] of [["invoice_payment_iban", form.iban.replace(/\s+/g, "").toUpperCase()], ["invoice_payment_bic", form.bic.trim().toUpperCase()], ["invoice_payment_holder", form.holder.trim()]] as const) {
      await api.rpc("platform_set_default", { p_key: key, p_value: wert });
    }
    setBank(null);
    await q.refetch();
  }, "Bankverbindung gespeichert.");
  const verbuchen = () => run("buchen", async () => {
    if (!buchen) return;
    if (!api.zahlungseingang) throw new Error("Verbuchen braucht die Serverfunktion zahlungseingang (nicht in der Demo).");
    await api.zahlungseingang({ action: "manuell", invoice: buchen.id, note: notiz.trim(), payment: eingang || null });
    setBuchen(null); setNotiz(""); setEingang("");
    await q.refetch();
  }, "Zahlung verbucht; die Rechnung ist bei Stripe als bezahlt markiert.");
  const offeneEingaenge = (d?.payments ?? []).filter((p) => p.status === "unclear" || p.status === "foreign");

  return (
    <>
      <title>Zahlung auf Rechnung · Plattform · CreatorOffice</title>
      <h1>Zahlung auf Rechnung</h1>
      <p>Abos auf Rechnung: Stripe stellt die Rechnungen mit 14 Tagen Zahlungsziel aus, überwiesen wird auf das Konto unten mit der Rechnungsnummer als Verwendungszweck.
        Weitergeleitete Payoneer-Mails verbucht die Serverfunktion „zahlungseingang“ selbst, Unklares landet als Ticket unter Support.</p>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : (
        <>
          <section className="panel stack" aria-labelledby="bank-h">
            <h2 id="bank-h">Bankverbindung</h2>
            <p>{d!.bank ? <span className="pill success"><span className="pill-zeichen">✓</span> Die Zahlungsart „Auf Rechnung“ ist wählbar.</span>
              : <span className="pill"><span className="pill-zeichen">○</span> Nicht wählbar: Kontoinhaber und eine gültige IBAN fehlen.</span>}</p>
            <div className="tk-felder">
              <label>Kontoinhaber<input value={form.holder} maxLength={70} onChange={(e) => setBank({ ...form, holder: e.target.value })} /></label>
              <label>IBAN<input value={form.iban} className="mono" maxLength={42} spellCheck={false} onChange={(e) => setBank({ ...form, iban: e.target.value })} /></label>
              <label>BIC<input value={form.bic} className="mono" maxLength={11} spellCheck={false} onChange={(e) => setBank({ ...form, bic: e.target.value })} /></label>
            </div>
            <small>Steht auf jeder Rechnung als Zusatzfeld beim Kunden; geänderte Angaben gelten für neue Abschlüsse und Umstellungen. Leer lassen schaltet die Zahlungsart ab.</small>
            <div className="row"><button type="button" className="primary" disabled={!!busy || !bank} aria-busy={busy === "bank"} onClick={() => void speichern()}>{busy === "bank" ? "Wird gespeichert …" : "Speichern"}</button></div>
          </section>

          <h2>Rechnungen</h2>
          {d!.invoices.length === 0 ? <div className="panel tk-empty"><p>Noch keine Rechnungen auf Rechnung.</p></div> : (
            <div className="tablewrap">
              <table>
                <caption className="sr-only">Rechnungen der Abos auf Rechnung</caption>
                <thead><tr><th scope="col">Konto</th><th scope="col">Rechnung</th><th scope="col">Betrag</th><th scope="col">Fällig</th><th scope="col">Stand</th><th scope="col"><span className="sr-only">Aktion</span></th></tr></thead>
                <tbody>
                  {d!.invoices.map((r) => (
                    <tr key={r.id}>
                      <td><Link to={`/intern/agenturen/${r.org_id}`}>{r.org_name}</Link>{r.customer_name && <><br /><small>{r.customer_name}</small></>}</td>
                      <td>{r.hosted_invoice_url ? <a href={r.hosted_invoice_url} target="_blank" rel="noreferrer" className="mono">{r.number ?? r.id}</a> : <span className="mono">{r.number ?? r.id}</span>}</td>
                      <td className="num">{betrag(r.status === "open" ? r.amount_remaining_cents : r.amount_due_cents, r.currency)}</td>
                      <td>{r.due_date ? date(r.due_date) : "–"}</td>
                      <td>{r.status === "open"
                        ? <span className={`pill ${r.overdue ? "error" : "info"}`}><span className="pill-zeichen">{r.overdue ? "!" : "◇"}</span> {r.overdue ? "überfällig" : "offen"}</span>
                        : r.status === "paid" ? <span className="pill success"><span className="pill-zeichen">✓</span> bezahlt{r.paid_via ? ` (${VIA[r.paid_via] ?? r.paid_via})` : ""}</span>
                        : <span className="pill"><span className="pill-zeichen">○</span> {r.status}</span>}</td>
                      <td>{r.status === "open" && <button type="button" disabled={!!busy} onClick={() => { setBuchen(r); setNotiz(""); setEingang(""); }}>Zahlung von Hand verbuchen</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>Zahlungseingänge</h2>
          {d!.payments.length === 0 ? <div className="panel tk-empty"><p>Noch keine Zahlungseingänge. Sie erscheinen, sobald die Payoneer-Mails an die Serverfunktion weitergeleitet werden.</p></div> : (
            <div className="tablewrap">
              <table>
                <caption className="sr-only">Zahlungseingänge</caption>
                <thead><tr><th scope="col">Eingang</th><th scope="col">Betrag</th><th scope="col">Zahler und Verwendungszweck</th><th scope="col">Stand</th><th scope="col">Konto</th></tr></thead>
                <tbody>
                  {d!.payments.map((p) => (
                    <tr key={p.id}>
                      <td>{date(p.received_at)}<br /><small>{p.source === "mail" ? `Mail${p.sender_verified ? ", Absender geprüft" : ", Absender nicht geprüft"}` : `von Hand${p.booked_by_email ? `, ${p.booked_by_email}` : ""}`}</small></td>
                      <td className="num">{betrag(p.amount_cents, p.currency)}</td>
                      <td>{p.payer ?? "–"}{p.reference && <><br /><small className="mono">{p.reference}</small></>}{p.note && <><br /><small>{p.note}</small></>}</td>
                      <td><span className={`pill ${p.status === "matched" ? "success" : p.status === "unclear" ? "error" : ""}`.trim()}>{STAND[p.status] ?? p.status}</span>
                        {p.reason && <><br /><small>{GRUND[p.reason] ?? p.reason}</small></>}
                        {p.ticket_id && <><br /><Link to={`/intern/tickets/${p.ticket_id}`}>Ticket öffnen</Link></>}</td>
                      <td>{p.org_name ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {error && !buchen && <p className="tk-error" role="alert">{error}</p>}
      <Dialog open={!!buchen} onClose={() => setBuchen(null)} title="Zahlung von Hand verbuchen">
        {buchen && (
          <div className="stack">
            <p>Rechnung <span className="mono">{buchen.number ?? buchen.id}</span> von {buchen.org_name} über {betrag(buchen.amount_remaining_cents, buchen.currency)}. Stripe markiert sie als außerhalb bezahlt; die Agentur ist danach wieder im normalen Stand.</p>
            <label className="stack">Woran erkannt (Pflicht)
              <input value={notiz} maxLength={300} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Payoneer-Referenz 123456789 vom 04.11." />
            </label>
            {offeneEingaenge.length > 0 && (
              <label className="stack">Erfassten Eingang zuordnen (optional)
                <select value={eingang} onChange={(e) => setEingang(e.target.value)}>
                  <option value="">keinen</option>
                  {offeneEingaenge.map((p) => <option key={p.id} value={p.id}>{date(p.received_at)} · {betrag(p.amount_cents, p.currency)} · {p.payer ?? "ohne Zahler"} · {STAND[p.status] ?? p.status}</option>)}
                </select>
              </label>
            )}
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button type="button" className="primary" disabled={!!busy || notiz.trim().length < 3} aria-busy={busy === "buchen"} onClick={() => void verbuchen()}>{busy === "buchen" ? "Wird verbucht …" : "Als bezahlt verbuchen"}</button>
              <button type="button" disabled={!!busy} onClick={() => setBuchen(null)}>Abbrechen</button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
